import { Analytics } from '@cobliteam/analytics';
import type { UUID } from '@cobliteam/common-types';
import { useUpdateOnboardingChecklist } from '@cobliteam/hooks/src/useEntity/useOnboardingChecklist';
import Button from '@cobliteam/ui/components/Button';
import Card, { CardContent } from '@cobliteam/ui/components/Card';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Icon from '@cobliteam/ui/icons';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import type { OnboardingSteps } from './OnboardingSteps';

interface CardItemProps {
  title: string;
  description: string;
  url: string;
  checked: boolean;
  step: OnboardingSteps;
  stepId: UUID;
  onCheckboxChange: (checked: boolean) => void;
}

const StepCard = ({ title, description, url, checked, step, stepId, onCheckboxChange }: CardItemProps) => {
  const history = useHistory();
  const { mutate: updateOnboardingChecklist } = useUpdateOnboardingChecklist();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCheckboxChange = useCallback(
    (newChecked: boolean) => {
      onCheckboxChange(newChecked);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        updateOnboardingChecklist({ id: stepId, finished: newChecked });
      }, 500);
    },
    [onCheckboxChange, stepId, updateOnboardingChecklist]
  );

  const handleClick = useCallback(() => {
    Analytics.track('Onboarding Checklist - Step card clicked', { step: step });
    history.push(url);
  }, [history, url, step]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Card>
      <CardContent className='!p-3 flex flex-row items-center gap-4'>
        <Checkbox onCheckedChange={handleCheckboxChange} checked={checked} className='ml-2' />

        <div className='flex flex-row items-center gap-2'>
          <Typography variant='text-lg-semibold' color='secondary'>
            {title}
          </Typography>
          <Tooltip content={description}>
            <Icon name='info' />
          </Tooltip>
        </div>

        <Button variant='ghost' color='primary' onClick={handleClick} className='ml-auto'>
          <Icon name='arrow-right' size='md' />
        </Button>
      </CardContent>
    </Card>
  );
};

export default memo(StepCard);
